import { useState, useCallback, useEffect } from "react";
import type { User, AuthState, LoginResponse } from "../types";
import api from "../services/authApi.ts";

export function useAuth() {
  const THIRTY_MINUTES_MS = 60 * 60 * 1000;

  const [authState, setAuthState] = useState<AuthState>(() => {
    const savedUser = localStorage.getItem("pos_user");
    const savedToken = localStorage.getItem("pos_access_token");
    const savedLocation = localStorage.getItem("pos_location");
    const savedMode = localStorage.getItem("pos_mode");

    if (savedUser && savedToken) {
      try {
        const user = JSON.parse(savedUser);
        return {
          isAuthenticated: true,
          user,
          location: savedLocation ? Number(savedLocation) : null,
          mode: savedMode || null,
        };
      } catch {
        localStorage.removeItem("pos_user");
        localStorage.removeItem("pos_access_token");
      }
    }

    return { isAuthenticated: false, user: null };
  });

  // LOGIN
  const login = useCallback(
    async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await api.post<LoginResponse>("/auth/authenticate", {
          Username: username,
          Password: password,
        });

        const data = response.data;

        if (data.Success && data.Data) {
          const nameParts = data.Data.Username.split("@")[0].split(".");
          const firstName = nameParts[0] || "User";
          const lastName = nameParts[1] || "";

          const userData: User = {
            id: data.Data.UserId.toString(),
            username: data.Data.Username,
            email: data.Data.Email,
            firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
            lastName: lastName.charAt(0).toUpperCase() + lastName.slice(1),
            tenantId: "",
            applicationId: "",
            accessToken: data.Data.AccessToken,
            refreshToken: data.Data.RefreshToken,
          };

          setAuthState({ isAuthenticated: true, user: userData });
          localStorage.setItem("pos_user", JSON.stringify(userData));
          localStorage.setItem("pos_access_token", data.Data.AccessToken);
          localStorage.setItem("pos_refresh_token", data.Data.RefreshToken);
          localStorage.setItem("pos_token_expiry", (Date.now() + THIRTY_MINUTES_MS).toString());

          // 🔥 CRITICAL FIX FOR POS 401
          sessionStorage.setItem("pos_auth_trigger", Date.now().toString());

          return { success: true };
        }

        const errorMessage =
          data.Errors?.[0] ||
          data.Messages?.[0] ||
          "Login failed. Please check your credentials.";
        return { success: false, error: errorMessage };
      } catch (error: unknown) {
        console.error("Login error:", error);

        if (error instanceof Error && error.message.includes("Network")) {
          return { success: false, error: "Network error. Please check your connection." };
        }

        return { success: false, error: "Unexpected error during login." };
      }
    },
    [THIRTY_MINUTES_MS]
  );

  // LOGOUT
  const logout = useCallback(() => {
    setAuthState({ isAuthenticated: false, user: null });
    localStorage.clear();
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("pos_access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const setLocation = useCallback((location: number) => {
    setAuthState((prev) => ({ ...prev, location }));
    localStorage.setItem("pos_location", location.toString());
  }, []);

  const setMode = useCallback((mode: string) => {
    setAuthState((prev) => ({ ...prev, mode }));
    localStorage.setItem("pos_mode", mode);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const expiry = localStorage.getItem("pos_token_expiry");
      if (expiry && Date.now() > parseInt(expiry)) {
        logout();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [logout]);

  return {
    ...authState,
    login,
    logout,
    getAuthHeaders,
    setLocation,
    setMode,
  };
}
